import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { api, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { Upload, Download, Eye, Check, X, Trash2 } from "lucide-react";

const TABS = ["All", "Pending", "Approved", "Rejected"];

function StatusBadge({ status }) {
  const cls = {
    Pending: "badge badge-pending",
    Approved: "badge badge-approved",
    Rejected: "badge badge-rejected",
  }[status] || "badge";
  return <span className={cls}>{status}</span>;
}

export default function AdminCustomers() {
  const [tab, setTab] = useState("All");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/customers", { params: tab === "All" ? {} : { status: tab } });
    setRows(r.data);
    setLoading(false);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  async function updateStatus(id, status) {
    try {
      const r = await api.patch(`/customers/${id}/status`, { status });
      const sync = r.data.shopify_sync;
      if (status === "Approved") {
        if (sync?.success) toast.success(`Approved. ${sync.message}`);
        else toast.success("Approved. " + (sync?.message || "Configure Shopify to sync tags."));
      } else {
        toast.success(`Customer ${status.toLowerCase()}`);
      }
      load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this customer?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    load();
  }

  function exportCSV() {
    window.open(`${API}/customers/export/csv`, "_blank");
  }

  async function importCSV(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/customers/import/csv`, { method: "POST", body: fd });
      const data = await r.json();
      toast.success(`Imported ${data.imported} customers`);
      load();
    } catch {
      toast.error("Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const counts = useMemo(() => {
    const c = { All: rows.length, Pending: 0, Approved: 0, Rejected: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div data-testid="admin-customers-page">
      <PageHeader
        title="Customers"
        subtitle="Manage wholesale customer applications and approvals"
        actions={
          <>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCSV} data-testid="customers-import-input" />
            <button className="btn-secondary inline-flex items-center gap-2" onClick={() => fileRef.current?.click()} data-testid="customers-import-btn">
              <Upload size={14} /> Import
            </button>
            <button className="btn-secondary inline-flex items-center gap-2" onClick={exportCSV} data-testid="customers-export-btn">
              <Download size={14} /> Export
            </button>
          </>
        }
      />

      <div className="b2b-card">
        <div className="flex border-b" style={{ borderColor: "var(--brand-border)" }} data-testid="customers-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              className={`b2b-tab ${tab === t ? "active" : ""}`}
              onClick={() => setTab(t)}
              data-testid={`customers-tab-${t.toLowerCase()}`}
            >
              {t} {tab === t ? `(${rows.length})` : ""}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--brand-muted)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No customers yet" subtitle="Applications will appear here as buyers register." testId="customers-empty" />
        ) : (
          <table className="b2b-table" data-testid="customers-table">
            <thead>
              <tr>
                <th>Full name</th>
                <th>Email</th>
                <th>Tags</th>
                <th>Form</th>
                <th>Status</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                  <td className="font-medium">{c.first_name} {c.last_name}</td>
                  <td>{c.email}</td>
                  <td>
                    {(c.tags || []).map((t) => (
                      <span key={t} className="badge badge-approved mr-1" style={{ background: "#e6ecf7", color: "#1a2340" }}>{t}</span>
                    ))}
                  </td>
                  <td>
                    <button
                      className="text-sm underline"
                      style={{ color: "var(--brand-navy)" }}
                      onClick={() => setViewing(c)}
                      data-testid={`customer-view-${c.id}`}
                    >
                      <Eye size={14} className="inline mr-1" /> View
                    </button>
                  </td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="text-sm" style={{ color: "var(--brand-muted)" }}>
                    {new Date(c.submitted_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center gap-2 justify-end">
                      {c.status !== "Approved" && (
                        <button className="btn-approve" onClick={() => updateStatus(c.id, "Approved")} data-testid={`customer-approve-${c.id}`}>
                          <Check size={13} className="inline mr-1" /> Approve
                        </button>
                      )}
                      {c.status !== "Rejected" && (
                        <button className="btn-reject" onClick={() => updateStatus(c.id, "Rejected")} data-testid={`customer-reject-${c.id}`}>
                          <X size={13} className="inline mr-1" /> Reject
                        </button>
                      )}
                      <button className="btn-danger" onClick={() => remove(c.id)} data-testid={`customer-delete-${c.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        title="Customer application"
        testId="customer-detail-modal"
        footer={
          <button className="btn-secondary" onClick={() => setViewing(null)} data-testid="customer-detail-close">
            Close
          </button>
        }
      >
        {viewing && (
          <div className="space-y-3 text-sm">
            <Row label="Name" value={`${viewing.first_name} ${viewing.last_name}`} />
            <Row label="Email" value={viewing.email} />
            <Row label="Phone" value={viewing.phone} />
            <Row label="Company" value={viewing.company_name} />
            <Row label="Website" value={viewing.company_website} />
            <Row label="Tax ID" value={viewing.tax_id} />
            <Row label="Address" value={`${viewing.address1} ${viewing.address2 || ""}, ${viewing.city}, ${viewing.state} ${viewing.zip_code}, ${viewing.country}`} />
            <Row label="Status" value={<StatusBadge status={viewing.status} />} />
            {viewing.document_name && (
              <div className="pt-2">
                <a
                  href={`${API}/customers/${viewing.id}/document`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="customer-download-doc"
                >
                  <Download size={14} /> Download: {viewing.document_name}
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-gray-100 last:border-0">
      <div className="w-32 text-xs uppercase tracking-wide" style={{ color: "var(--brand-muted)" }}>{label}</div>
      <div className="flex-1 text-right">{value || <span className="text-gray-400">—</span>}</div>
    </div>
  );
}
